import React, { useEffect, useState } from "react";
import { api } from "@/lib/http";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtCurrency } from "@/lib/calc";
import { toast } from "sonner";

// Take part or full payment against an invoice that's on the client's account — cash, card, bank or their credit balance.
export const TakePaymentDialog = ({ sale, clientId, cur, onClose, onDone }) => {
  const [amount, setAmount] = useState(String(sale?.amount_owing ?? ""));
  const [method, setMethod] = useState("cash");
  const [note, setNote] = useState("");
  const [credit, setCredit] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (clientId) api.get(`/clients/${clientId}/owing`).then(({ data }) => setCredit(data.credit_balance || 0)).catch(() => {}); }, [clientId]);
  const owing = sale?.amount_owing || 0;
  const val = Math.min(parseFloat(amount) || 0, owing, method === "credit" ? credit : Infinity);
  const left = Math.max(0, owing - val);
  const submit = async () => {
    if (!(val > 0)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/sales/${sale.id}/payments`, { amount: val, method, note });
      toast.success(data.settled ? `Invoice #${sale.invoice_no} paid in full` : `${fmtCurrency(data.paid, cur, 2)} taken · ${fmtCurrency(data.owing, cur, 2)} still owing`);
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't record the payment"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!sale} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="take-payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Take payment · invoice #{sale?.invoice_no}</DialogTitle>
          <DialogDescription>{fmtCurrency(owing, cur, 2)} owing. Take all of it or part of it — the rest stays on the account.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1"><label className="text-xs text-muted-foreground">Amount</label>
              <Input type="number" inputMode="decimal" min="0" max={owing} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-10 rounded-lg tabular-nums" data-testid="take-payment-amount" /></div>
            <div className="space-y-1"><label className="text-xs text-muted-foreground">Paid by</label>
              <Select value={method} onValueChange={setMethod}><SelectTrigger className="h-10 rounded-lg" data-testid="take-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="card_reader">Card</SelectItem>
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  <SelectItem value="credit" disabled={!(credit > 0)} data-testid="take-payment-method-credit">Client credit{credit > 0 ? ` (${fmtCurrency(credit, cur, 2)} available)` : " (none)"}</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent></Select></div>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" className="rounded-lg" onClick={() => setAmount(String(owing))} data-testid="take-payment-full">Full {fmtCurrency(owing, cur, 2)}</Button>
            <Button type="button" variant="outline" size="sm" className="rounded-lg" onClick={() => setAmount((owing / 2).toFixed(2))} data-testid="take-payment-half">Half</Button>
            {credit > 0 && <Button type="button" variant="outline" size="sm" className="rounded-lg" onClick={() => { setMethod("credit"); setAmount(String(Math.min(credit, owing))); }} data-testid="take-payment-use-credit">Use credit</Button>}
          </div>
          <Input placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="h-9 rounded-lg" data-testid="take-payment-note" />
          <p className="text-sm" data-testid="take-payment-summary">Taking <b className="tabular-nums">{fmtCurrency(val, cur, 2)}</b>{method === "credit" ? " from credit" : ""} · {left > 0.005 ? <span className="text-amber-700">{fmtCurrency(left, cur, 2)} will still be owing</span> : <span className="text-emerald-700">invoice will be paid in full</span>}</p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" className="rounded-lg" onClick={onClose} data-testid="take-payment-cancel">Cancel</Button>
          <Button className="rounded-lg" onClick={submit} disabled={busy || !(val > 0)} data-testid="take-payment-confirm">{busy ? "Recording…" : "Record payment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
